/**
 * 🔄 Hook reutilizable para paginación (CORE)
 */

import { useState, useCallback, useMemo } from 'react';

export interface PaginationState {
  page: number;
  pageSize: number;
  total: number | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface UsePaginationOptions {
  initialPage?: number;
  initialPageSize?: number;
  total?: number | null;
}

export interface UsePaginationReturn {
  page: number;
  pageSize: number;
  offset: number;
  total: number | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setTotal: (total: number | null) => void;
  nextPage: () => void;
  previousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  range: { from: number; to: number };
  totalPages: number | null;
}

export function usePagination(options: UsePaginationOptions = {}): UsePaginationReturn {
  /** 25 por defecto; debe coincidir con DEFAULT_LIST_PAGE_SIZE en la plantilla ListView. */
  const {
    initialPage = 1,
    initialPageSize = 25,
    total: initialTotal = null,
  } = options;

  const [page, setPageState] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [total, setTotal] = useState<number | null>(initialTotal);

  const offset = useMemo(() => (page - 1) * pageSize, [page, pageSize]);
  const totalPages = useMemo(() => {
    if (total === null) return null;
    return Math.ceil(total / pageSize);
  }, [total, pageSize]);

  const hasNextPage = useMemo(() => {
    if (total === null) return false;
    return page < totalPages!;
  }, [page, total, totalPages]);

  const hasPreviousPage = useMemo(() => page > 1, [page]);

  const range = useMemo(() => {
    const from = offset + 1;
    const to = total === null ? offset + pageSize : Math.min(offset + pageSize, total);
    return { from, to };
  }, [offset, pageSize, total]);

  const setPage = useCallback((newPage: number) => {
    if (newPage < 1) return;
    if (total !== null && totalPages !== null && newPage > totalPages) return;
    setPageState(newPage);
  }, [total, totalPages]);

  const setPageSize = useCallback((newSize: number) => {
    if (newSize < 1) return;
    setPageSizeState(newSize);
    setPageState(1);
  }, []);

  const nextPage = useCallback(() => {
    if (hasNextPage) setPageState((prev) => prev + 1);
  }, [hasNextPage]);

  const previousPage = useCallback(() => {
    if (hasPreviousPage) setPageState((prev) => prev - 1);
  }, [hasPreviousPage]);

  const goToFirstPage = useCallback(() => setPageState(1), []);
  const goToLastPage = useCallback(() => {
    if (totalPages !== null) setPageState(totalPages);
  }, [totalPages]);

  const setTotalValue = useCallback((newTotal: number | null) => setTotal(newTotal), []);

  return {
    page,
    pageSize,
    offset,
    total,
    hasNextPage,
    hasPreviousPage,
    setPage,
    setPageSize,
    setTotal: setTotalValue,
    nextPage,
    previousPage,
    goToFirstPage,
    goToLastPage,
    range,
    totalPages,
  };
}
